import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../services/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import RexiPopup from '../../../components/RexiPopup';
import SmartPagination from '../../../components/SmartPagination';
import LabSelector from '../../../components/LabSelector';
import '../DataTableTemplate.css';
import './TestReports.css';


interface TestReport {
  id: number;
  factory_id?: number;
  cpsc_lab_id?: number;
  other_lab_id?: number;
  report_number?: string;
  report_date?: string;
  products?: Product[];
}

interface Factory {
  id: number;
  name: string;
}

interface Product {
  id: number;
  upc?: number;
  name?: string;
  status: 'active' | 'inactive';
  certificate_type?: 'GCC' | 'CPC';
}

interface CitationCode {
  id: number;
  citation_code: string;
  citation_code_description: string;
  status: 'active' | 'inactive';
  applies_to_gcc: boolean;
  applies_to_cpc: boolean;
}

// Moved outside component to prevent recreation on every render
const FilterInput = ({ label, placeholder, column, value, onChange }: {
  label: string;
  placeholder: string;
  column: string;
  value: string;
  onChange: (column: string, value: string) => void;
}) => (
  <div className="filter-group">
    <label className="filter-label">{label}</label>
    <input
      type="text"
      className="filter-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(column, e.target.value)}
    />
  </div>
);

// Helper function to generate modal help message
const getModalHelpMessage = (editingReport: boolean, selectedProducts: any[], getSelectedProductsCertificateType: () => string) => {
  if (editingReport) {
    const reportStatus = selectedProducts.length > 0 
      ? `contains products with a ${getSelectedProductsCertificateType()} certificate type` 
      : 'is being edited';
    return `This test report ${reportStatus}. You can only add products of the same certificate type (either all GCC or all CPC). Once you select your first product, the dropdown will automatically filter to show only products with the same certificate type.\n\nCitation codes are also filtered to show only those applicable to the certificate type.\n\nFor products requiring a CPC, only CPSC certified labs can be selected.`;
  } else {
    return "You can ask me to analyze your test report or manually add it here. Click the Ask Rexi menu button if you would like me to review and extract data from your report.\n\nTest reports can only include products of the same certificate type (either all GCC or all CPC). Once you select your first product, the dropdown will automatically filter to show only products with the same certificate type.\n\nCitation codes will also be filtered to show only those applicable to the certificate type.\n\nFor products requiring a CPC, only CPSC certified labs can be selected.";
  }
};

const TestReports = () => {
  const { user } = useAuth();
  const [testReports, setTestReports] = useState([]);
  const [factories, setFactories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [citationCodes, setCitationCodes] = useState([]);
  const [selectedCitationCodes, setSelectedCitationCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columnFilters, setColumnFilters] = useState({
    report_number: '',
    factory_id: '',
    cpsc_lab_id: '',
    other_lab_id: '',
    product_upc: '',
    product_name: '',
    certificate_type: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'report_date', direction: 'desc' });
  const [popupState, setPopupState] = useState({
    help: false,
    delete: false,
    modal: false,
    modalHelp: false,
    foreignKeyError: false
  });
  const [reportToDelete, setReportToDelete] = useState(null);

  const [editingReport, setEditingReport] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [expandedReports, setExpandedReports] = useState(new Set());
  const [selectedLab, setSelectedLab] = useState(null);
  const [formData, setFormData] = useState({
    factory_id: '',
    report_number: '',
    report_date: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Fetch test reports from Supabase with products
  const fetchTestReports = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('test_reports')
        .select(`
          id,
          factory_id,
          cpsc_lab_id,
          other_lab_id,
          report_number,
          report_date,
          auth_id,
          test_report_products (
            product_id,
            products (id, upc, name, status, certificate_type)
          )
        `)
        .eq('auth_id', user?.id)
        .order('id', { ascending: false });

      if (error) {
        throw error;
      }

      // Transform data to include products array
      const reportsWithProducts = data?.map((report: any) => ({
        ...report,
        products: report.test_report_products?.map((trp: any) => trp.products).filter(Boolean) || []
      })) || [];

      setTestReports(reportsWithProducts);
      
      // Set all reports with products as expanded by default
      const reportsWithProductsIds = new Set(
        reportsWithProducts
          .filter(report => report.products && report.products.length > 0)
          .map(report => report.id)
      );
      setExpandedReports(reportsWithProductsIds);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching test reports:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch factories from Supabase
  const fetchFactories = async () => {
    try {
      const { data, error } = await supabase
        .from('factories')
        .select('id, name')
        .eq('status', 'active')
        .eq('auth_id', user?.id)
        .order('name', { ascending: true });

      if (error) {
        throw error;
      }

      setFactories(data || []);
    } catch (err: any) {
      console.error('Error fetching factories:', err);
    }
  };

  // Fetch products from Supabase
  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, upc, name, status, certificate_type')
        .eq('status', 'active')
        .eq('auth_id', user?.id)
        .order('name', { ascending: true });

      if (error) {
        throw error;
      }

      setProducts(data || []);
    } catch (err: any) {
      console.error('Error fetching products:', err);
    }
  };

  // Fetch citation codes from Supabase
  const fetchCitationCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('citation_codes')
        .select('id, citation_code, citation_code_description, status, applies_to_gcc, applies_to_cpc')
        .eq('status', 'active')
        .order('citation_code', { ascending: true });

      if (error) {
        throw error;
      }

      setCitationCodes(data || []);
    } catch (err: any) {
      console.error('Error fetching citation codes:', err);
    }
  };

  // Fetch test report products for editing
  const fetchTestReportProducts = async (testReportId: number) => {
    try {
      const { data, error } = await supabase
        .from('test_report_products')
        .select(`
          product_id,
          products (id, upc, name, status, certificate_type)
        `)
        .eq('test_report_id', testReportId);

      if (error) {
        throw error;
      }

      const selectedProds = data?.map((item: any) => item.products).filter(Boolean) || [];
      setSelectedProducts(selectedProds);
    } catch (err: any) {
      console.error('Error fetching test report products:', err);
    }
  };

  // Fetch test report citation codes for editing
  const fetchTestReportCitationCodes = async (testReportId: number) => {
    try {
      const { data, error } = await supabase
        .from('test_report_citation_codes')
        .select(`
          citation_code_id,
          citation_codes (id, citation_code, citation_code_description, status, applies_to_gcc, applies_to_cpc)
        `)
        .eq('test_report_id', testReportId);

      if (error) {
        throw error;
      }

      const selectedCodes = data?.map((item: any) => item.citation_codes).filter(Boolean) || [];
      setSelectedCitationCodes(selectedCodes);
    } catch (err: any) {
      console.error('Error fetching test report citation codes:', err);
    }
  };

  // Fetch lab data for editing
  const fetchTestReportLab = async (report: TestReport) => {
    try {
      if (report.cpsc_lab_id) {
        // Fetch from certified_labs
        const { data, error } = await supabase
          .from('certified_labs')
          .select('id, name, cpsc_id, city, state_province, country')
          .eq('id', report.cpsc_lab_id)
          .single();

        if (error) throw error;
        if (data) {
          setSelectedLab({ ...data, source: 'certified' });
        }
      } else if (report.other_lab_id) {
        // Fetch from test_labs
        const { data, error } = await supabase
          .from('test_labs')
          .select('id, name, cpsc_id, city, state_province, country')
          .eq('id', report.other_lab_id)
          .eq('auth_id', user?.id)
          .single();

        if (error) throw error;
        if (data) {
          setSelectedLab({ ...data, source: 'user' });
        }
      } else {
        setSelectedLab(null);
      }
    } catch (err: any) {
      console.error('Error fetching test report lab:', err);
      setSelectedLab(null);
    }
  };

  useEffect(() => {
    fetchTestReports();
    fetchFactories();
    fetchProducts();
    fetchCitationCodes();
  }, []);

  // Helper functions for popup management
  const togglePopup = (popup: keyof typeof popupState, show: boolean) => {
    setPopupState((prev: typeof popupState) => ({ ...prev, [popup]: show }));
  };

  // Toggle expand/collapse for test report products
  const toggleReportExpansion = (reportId: number) => {
    setExpandedReports((prev: Set<number>) => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  // Table configuration - drives both headers and data rendering
  const tableColumns = [
    { key: 'expand', label: '', sortable: false, className: 'expand', showInTable: true },
    { key: 'report_number', label: 'Report Number', sortable: false, className: 'report_number', showInTable: true },
    { key: 'factory_id', label: 'Factory', sortable: false, className: 'factory_id', showInTable: true },
    { key: 'cpsc_lab_id', label: 'CPSC Lab ID', sortable: false, className: 'cpsc_lab_id', showInTable: true },
    { key: 'other_lab_id', label: 'Other Lab ID', sortable: false, className: 'other_lab_id', showInTable: true },
    { key: 'report_date', label: 'Report Date', sortable: true, className: 'report_date', showInTable: true },
    { key: 'actions', label: 'Actions', sortable: false, className: 'actions', showInTable: true }
  ];

  // Filter test reports based on column filters
  const filteredTestReports = testReports.filter((report: TestReport) => {
    const matchesReportNumber = !columnFilters.report_number || 
      report.report_number?.toLowerCase().includes(columnFilters.report_number.toLowerCase());
    
    const matchesFactoryId = !columnFilters.factory_id || (() => {
      const factory = factories.find((f: Factory) => f.id === report.factory_id);
      return factory?.name?.toLowerCase().includes(columnFilters.factory_id.toLowerCase());
    })();
    
    const matchesCpscLabId = !columnFilters.cpsc_lab_id || 
      report.cpsc_lab_id?.toString().includes(columnFilters.cpsc_lab_id);
    
    const matchesOtherLabId = !columnFilters.other_lab_id || 
      report.other_lab_id?.toString().includes(columnFilters.other_lab_id);
    
    // Product-based filters
    const matchesProductUpc = !columnFilters.product_upc || 
      (report.products && report.products.some((product: Product) => 
        product.upc?.toString().toLowerCase().includes(columnFilters.product_upc.toLowerCase())
      ));
    
    const matchesProductName = !columnFilters.product_name || 
      (report.products && report.products.some((product: Product) => 
        product.name?.toLowerCase().includes(columnFilters.product_name.toLowerCase())
      ));
    
    const matchesCertificateType = !columnFilters.certificate_type || 
      (report.products && Array.isArray(report.products) && report.products.some((product: Product) => 
        product.certificate_type === columnFilters.certificate_type
      ));
    
    return matchesReportNumber && matchesFactoryId && matchesCpscLabId && matchesOtherLabId && 
           matchesProductUpc && matchesProductName && matchesCertificateType;
  });

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters((prev: typeof columnFilters) => ({ ...prev, [column]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearAllFilters = () => {
    setColumnFilters({
      report_number: '',
      factory_id: '',
      cpsc_lab_id: '',
      other_lab_id: '',
      product_upc: '',
      product_name: '',
      certificate_type: ''
    });
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reusable interactive cell component
  const InteractiveCell = ({ children, className = "", dataLabel = "" }: any) => (
    <div className={`table-cell ${className}`} data-label={dataLabel}>
      <div
        className="table-cell-input"
        contentEditable
        suppressContentEditableWarning={true}
        onInput={(e: any) => e.preventDefault()}
        onKeyDown={(e: any) => {
          const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Escape'];
          const isCopyOperation = e.ctrlKey && (e.key === 'c' || e.key === 'a');
          if (!allowedKeys.includes(e.key) && !isCopyOperation) {
            e.preventDefault();
          }
        }}
        onPaste={(e: any) => e.preventDefault()}
        onMouseDown={(e: any) => e.stopPropagation()}
        onBlur={(e: any) => e.currentTarget.scrollLeft = 0}
      >
        {children}
      </div>
    </div>
  );

  // Reusable table header cell component
  const TableHeaderCell = ({ column }: any) => (
    <div className={`table-cell ${column.className || ''}`}>
      {column.sortable ? (
        <div className="sortable-header" onClick={() => handleSort(column.key)}>
          {column.label}
          <span className={`sort-icon ${getSortIcon(column.key)} ${sortConfig.key === column.key ? 'active' : ''}`}></span>
        </div>
      ) : (
        column.label
      )}
    </div>
  );

  // Reusable table data cell component
  const TableDataCell = ({ column, report }: any) => {
    if (column.key === 'expand') {
      const isExpanded = expandedReports.has(report.id);
      const hasProducts = report.products && report.products.length > 0;
      return (
        <div className="table-cell" data-label="">
          {hasProducts && (
            <button 
              className="expand-btn"
              onClick={() => toggleReportExpansion(report.id)}
              title={isExpanded ? 'Collapse products' : 'Show products'}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
        </div>
      );
    }

    if (column.key === 'actions') {
      return (
        <div className="table-cell" data-label="Actions">
          <div className="table-actions">
            <button className="table-action-btn table-edit-btn" onClick={() => openEditModal(report)}>
              Details
            </button>
            <button className="table-action-btn table-delete-btn" onClick={() => confirmDelete(report)}>
              Delete
            </button>
          </div>
        </div>
      );
    }

    if (column.key === 'report_date') {
      return (
        <div className="table-cell report_date" data-label="Report Date">
          {report.report_date ? new Date(report.report_date + 'T00:00:00').toLocaleDateString() : '-'}
        </div>
      );
    }

    if (column.key === 'factory_id') {
      const factory = factories.find((f: Factory) => f.id === report.factory_id);
      return (
        <InteractiveCell className={column.className || ''} dataLabel={column.label}>
          {factory ? factory.name : (report.factory_id || '-')}
        </InteractiveCell>
      );
    }

    // Regular interactive cell
    return (
      <InteractiveCell className={column.className || ''} dataLabel={column.label}>
        {(report as any)[column.key] || '-'}
      </InteractiveCell>
    );
  };

  const hasActiveFilters = Object.values(columnFilters).some(filter => filter && filter !== 'all');

  // Sorting functionality
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to filtered results
  const sortedTestReports = useMemo(() => {
    if (!sortConfig.key) return filteredTestReports;

    return [...filteredTestReports].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      if (sortConfig.key === 'report_date') {
        aValue = new Date(a[sortConfig.key] || '').getTime();
        bValue = new Date(b[sortConfig.key] || '').getTime();
      } else {
        aValue = (a[sortConfig.key] || '').toString().toLowerCase();
        bValue = (b[sortConfig.key] || '').toString().toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredTestReports, sortConfig]);

  // Apply pagination to sorted results
  const paginatedTestReports = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedTestReports.slice(startIndex, endIndex);
  }, [sortedTestReports, currentPage, itemsPerPage]);

  // Calculate pagination info
  const totalPages = Math.ceil(sortedTestReports.length / itemsPerPage);
  const totalItems = sortedTestReports.length;

  const getSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) {
      return 'none';
    }
    return sortConfig.direction;
  };

  // Handle delete test report
  const handleDeleteTestReport = async () => {
    if (!reportToDelete) return;

    try {
      // First, delete related records in the junction tables
      // Delete test report products
      const { error: productsError } = await supabase
        .from('test_report_products')
        .delete()
        .eq('test_report_id', reportToDelete.id);

      if (productsError) {
        throw new Error(`Error deleting test report products: ${productsError.message}`);
      }

      // Delete test report citation codes
      const { error: citationError } = await supabase
        .from('test_report_citation_codes')
        .delete()
        .eq('test_report_id', reportToDelete.id);

      if (citationError) {
        throw new Error(`Error deleting test report citation codes: ${citationError.message}`);
      }

      // Finally, delete the test report itself
      const { error: reportError } = await supabase
        .from('test_reports')
        .delete()
        .eq('id', reportToDelete.id)
        .eq('auth_id', user?.id);

      if (reportError) {
        throw new Error(`Error deleting test report: ${reportError.message}`);
      }

      setTestReports(testReports.filter((r: TestReport) => r.id !== reportToDelete.id));
      togglePopup('delete', false);
      setReportToDelete(null);
    } catch (err: any) {
      // Check if this is a foreign key constraint error
      if (err.message && err.message.includes('foreign key constraint') && err.message.includes('product_certification_test_reports')) {
        togglePopup('delete', false);
        togglePopup('foreignKeyError', true);
      } else {
        setError(err.message);
      }
      console.error('Error deleting test report:', err);
    }
  };

  const confirmDelete = (report: TestReport) => {
    setReportToDelete(report);
    togglePopup('delete', true);
  };

  // Get the certificate type of currently selected products
  const getSelectedProductsCertificateType = () => {
    if (selectedProducts.length === 0) return null;
    const firstProductCertType = selectedProducts[0]?.certificate_type;
    return firstProductCertType === 'GCC' || firstProductCertType === 'CPC' ? firstProductCertType : null;
  };

  // Get filtered products based on certificate type constraints
  const getFilteredProducts = () => {
    // First filter: only show products with GCC or CPC certificate types
    const validProducts = products.filter((p: Product) => 
      p.certificate_type === 'GCC' || p.certificate_type === 'CPC'
    );

    // If no products selected yet, show all valid products
    if (selectedProducts.length === 0) {
      return validProducts;
    }

    // If products are already selected, only show products with the same certificate type
    const requiredCertType = getSelectedProductsCertificateType();
    if (!requiredCertType) {
      return validProducts; // Fallback if somehow we have invalid products selected
    }

    return validProducts.filter((p: Product) => p.certificate_type === requiredCertType);
  };

  // Product management functions
  const addProduct = (productId: number) => {
    const product = products.find((p: Product) => p.id === productId);
    if (!product) return;

    // Check if product is already selected
    if (selectedProducts.some((sp: Product) => sp.id === productId)) return;

    // Validate certificate type
    if (product.certificate_type !== 'GCC' && product.certificate_type !== 'CPC') {
      alert('Only products with GCC or CPC certificate types can be added to test reports.');
      return;
    }

    // If this is not the first product, ensure it matches the certificate type of existing products
    const existingCertType = getSelectedProductsCertificateType();
    if (existingCertType && product.certificate_type !== existingCertType) {
      alert(`All products in a test report must have the same certificate type. This report already contains ${existingCertType} products.`);
      return;
    }

    setSelectedProducts([...selectedProducts, product]);
    // Clear products validation error if it exists
    if (formErrors.products) {
      setFormErrors((prev: Record<string, string>) => ({ ...prev, products: '' }));
    }
  };

  const removeProduct = (productId: number) => {
    setSelectedProducts(selectedProducts.filter((p: Product) => p.id !== productId));
  };

  // Citation code management functions
  const addCitationCode = (citationCodeId: number) => {
    const citationCode = citationCodes.find((cc: CitationCode) => cc.id === citationCodeId);
    if (citationCode && !selectedCitationCodes.some((scc: CitationCode) => scc.id === citationCodeId)) {
      setSelectedCitationCodes([...selectedCitationCodes, citationCode]);
    }
  };

  const removeCitationCode = (citationCodeId: number) => {
    setSelectedCitationCodes(selectedCitationCodes.filter((cc: CitationCode) => cc.id !== citationCodeId));
  };

  // Get filtered citation codes based on selected products' certificate types
  const getFilteredCitationCodes = () => {
    if (selectedProducts.length === 0) {
      return citationCodes; // If no products selected, show all citation codes
    }

    // Get unique certificate types from selected products
    const certificateTypes = Array.from(new Set(selectedProducts.map((p: Product) => p.certificate_type).filter(Boolean)));
    
    // If no certificate types are specified in products, show all citation codes
    if (certificateTypes.length === 0) {
      return citationCodes;
    }

    // Filter citation codes based on certificate types
    return citationCodes.filter((cc: CitationCode) => {
      return certificateTypes.some(certType => {
        if (certType === 'GCC') return cc.applies_to_gcc;
        if (certType === 'CPC') return cc.applies_to_cpc;
        return false;
      });
    });
  };

  // Modal functions
  const openAddModal = () => {
    setEditingReport(null);
    setSelectedLab(null);
    setFormData({
      factory_id: '',
      report_number: '',
      report_date: ''
    });
    setSelectedProducts([]);
    setSelectedCitationCodes([]);
    setFormErrors({});
    togglePopup('modal', true);
    togglePopup('modalHelp', true);
  };

  const openEditModal = (report: TestReport) => {
    setEditingReport(report);
    setSelectedLab(null);
    setFormData({
      factory_id: report.factory_id?.toString() || '',
      report_number: report.report_number || '',
      report_date: report.report_date || ''
    });
    setSelectedProducts([]);
    setSelectedCitationCodes([]);
    setFormErrors({});
    // Fetch products, citation codes, and lab data for this test report
    if (report.id) {
      fetchTestReportProducts(report.id);
      fetchTestReportCitationCodes(report.id);
    }
    fetchTestReportLab(report);
    togglePopup('modal', true);
    togglePopup('modalHelp', true);
  };

  const closeModal = () => {
    togglePopup('modal', false);
    togglePopup('modalHelp', false); // Also close help popup when modal closes
    setEditingReport(null);
    setSelectedProducts([]);
    setSelectedCitationCodes([]);
    setFormErrors({});
    setSaving(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev: typeof formData) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev: Record<string, string>) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    // Required fields validation
    const requiredFields = [
      { field: 'report_number', label: 'Report number' }
    ];
    
    requiredFields.forEach(({ field, label }) => {
      if (!formData[field]?.trim()) {
        errors[field] = `${label} is required`;
      }
    });
    
    // Products validation
    if (selectedProducts.length === 0) {
      errors.products = 'At least one product is required';
    }
    
    // CPC certificate type requires CPSC certified lab
    const certificateType = getSelectedProductsCertificateType();
    if (certificateType === 'CPC') {
      if (!selectedLab || selectedLab.source !== 'certified') {
        errors.lab = 'A CPSC certified lab is required for products with CPC certificate type';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveTestReport = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const reportData: any = {
        factory_id: formData.factory_id ? parseInt(formData.factory_id) : null,
        report_number: formData.report_number || null,
        report_date: formData.report_date || null,
        auth_id: user?.id
      };

      // Set lab ID based on selected lab and its source, and clear the other field
      if (selectedLab?.source === 'certified') {
        reportData.cpsc_lab_id = selectedLab.id;
        reportData.other_lab_id = null;
      } else if (selectedLab?.source === 'user') {
        reportData.other_lab_id = selectedLab.id;
        reportData.cpsc_lab_id = null;
      } else {
        // No lab selected - clear both
        reportData.cpsc_lab_id = null;
        reportData.other_lab_id = null;
      }

      if (editingReport) {
        // Update existing test report
        const { error } = await supabase
          .from('test_reports')
          .update(reportData)
          .eq('id', editingReport.id)
          .eq('auth_id', user?.id);

        if (error) throw error;

        // Delete existing product associations
        const { error: deleteError } = await supabase
          .from('test_report_products')
          .delete()
          .eq('test_report_id', editingReport.id)
          .eq('auth_id', user?.id);

        if (deleteError) throw deleteError;

        // Delete existing citation code associations
        const { error: deleteCitationError } = await supabase
          .from('test_report_citation_codes')
          .delete()
          .eq('test_report_id', editingReport.id)
          .eq('auth_id', user?.id);

        if (deleteCitationError) throw deleteCitationError;

        // Insert new product associations
        if (selectedProducts.length > 0) {
          const productInserts = selectedProducts.map((product: Product) => ({
            test_report_id: editingReport.id,
            product_id: product.id,
            auth_id: user?.id
          }));

          const { error: insertError } = await supabase
            .from('test_report_products')
            .insert(productInserts);

          if (insertError) throw insertError;
        }

        // Insert new citation code associations
        if (selectedCitationCodes.length > 0) {
          const citationCodeInserts = selectedCitationCodes.map((citationCode: CitationCode) => ({
            test_report_id: editingReport.id,
            citation_code_id: citationCode.id,
            auth_id: user?.id
          }));

          const { error: insertCitationError } = await supabase
            .from('test_report_citation_codes')
            .insert(citationCodeInserts);

          if (insertCitationError) throw insertCitationError;
        }

        // Update the test report with new data and products
        setTestReports(testReports.map((r: TestReport) => 
          r.id === editingReport.id 
            ? { ...r, ...reportData, products: selectedProducts }
            : r
        ));
        
        // Update expanded state based on whether the report has products
        setExpandedReports((prev: Set<number>) => {
          const newSet = new Set(prev);
          if (selectedProducts.length > 0) {
            newSet.add(editingReport.id);
          } else {
            newSet.delete(editingReport.id);
          }
          return newSet;
        });
      } else {
        // Create new test report
        const { data, error } = await supabase
          .from('test_reports')
          .insert([reportData])
          .select()
          .single();

        if (error) throw error;

        // Insert product associations
        if (selectedProducts.length > 0) {
          const productInserts = selectedProducts.map((product: Product) => ({
            test_report_id: data.id,
            product_id: product.id,
            auth_id: user?.id
          }));

          const { error: insertError } = await supabase
            .from('test_report_products')
            .insert(productInserts);

          if (insertError) throw insertError;
        }

        // Insert citation code associations
        if (selectedCitationCodes.length > 0) {
          const citationCodeInserts = selectedCitationCodes.map((citationCode: CitationCode) => ({
            test_report_id: data.id,
            citation_code_id: citationCode.id,
            auth_id: user?.id
          }));

          const { error: insertCitationError } = await supabase
            .from('test_report_citation_codes')
            .insert(citationCodeInserts);

          if (insertCitationError) throw insertCitationError;
        }

        // Add the new test report with products to the list
        setTestReports([{ ...data, products: selectedProducts }, ...testReports]);
        
        // Expand the new test report if it has products
        if (selectedProducts.length > 0) {
          setExpandedReports((prev: Set<number>) => new Set([...Array.from(prev), data.id]));
        }
      }

      closeModal();
    } catch (err: any) {
      setError(err.message);
      console.error('Error saving test report:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = async () => {
    // Use filtered test reports (what's currently shown on page)
    const dataToExport = sortedTestReports;
    
    if (dataToExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Fetch citation codes for each test report
    const reportsWithDetails = await Promise.all(
      dataToExport.map(async (report: TestReport) => {
        try {
          // Fetch citation codes for this report
          const { data: citationData } = await supabase
            .from('test_report_citation_codes')
            .select(`
              citation_codes (citation_code, citation_code_description)
            `)
            .eq('test_report_id', report.id);

          const citationCodes = citationData?.map((item: any) => item.citation_codes).filter(Boolean) || [];

          // Get factory name
          const factory = factories.find((f: Factory) => f.id === report.factory_id);

          return {
            ...report,
            factory_name: factory?.name || '',
            citation_codes: citationCodes
          };
        } catch (error) {
          console.error('Error fetching details for report', report.id, error);
          return {
            ...report,
            factory_name: '',
            citation_codes: []
          };
        }
      })
    );

    // Define comprehensive headers
    const headers = [
      'Report Number',
      'Factory Name', 
      'CPSC Lab ID',
      'Other Lab ID',
      'Report Date',
      'Product UPCs',
      'Product Names',
      'Certificate Types',
      'Citation Codes',
      'Citation Descriptions'
    ];

    // Convert data to CSV format with products and citation codes
    const csvContent = [
      headers.join(','),
      ...reportsWithDetails.map((report: any) => {
        // Collect product data
        const products = report.products || [];
        const upcs = products.map((p: Product) => p.upc || '').join('; ');
        const names = products.map((p: Product) => p.name || '').join('; ');
        const certTypes = products.map((p: Product) => p.certificate_type || '').join('; ');
        
        // Collect citation code data
        const codes = report.citation_codes.map((c: any) => c.citation_code || '').join('; ');
        const descriptions = report.citation_codes.map((c: any) => c.citation_code_description || '').join('; ');

        return [
          `"${report.report_number || ''}"`,
          `"${report.factory_name || ''}"`,
          `"${report.cpsc_lab_id || ''}"`,
          `"${report.other_lab_id || ''}"`,
          `"${report.report_date || ''}"`,
          `"${upcs}"`,
          `"${names}"`,
          `"${certTypes}"`,
          `"${codes}"`,
          `"${descriptions}"`
        ].join(',');
      })
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `test_reports_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="data-table-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      <h1 className="data-table-title">Test Reports</h1>
      <div className="page-help-link" onClick={() => togglePopup('help', true)}>
        Ask Rexi about this page
      </div>

      {error && (
        <div className="error-state">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="test-reports-table-container">
        {/* Filter Toggle and Card */}
        <div className="test-reports-filter-section">
        <div className="filter-toggle-header">
          <button 
            className="filter-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? '▼' : '▶'} Filter Test Reports
            {hasActiveFilters && <span className="active-filter-indicator">({Object.values(columnFilters).filter(filter => filter && filter !== 'all').length} active)</span>}
          </button>
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              Clear All Filters
            </button>
          )}
        </div>
        
        {showFilters && (
          <div className="table-filters">
            <div className="filters-grid">
              <FilterInput 
                label="Report Number" 
                placeholder="Search by report number..." 
                column="report_number" 
                value={columnFilters.report_number}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Product UPC" 
                placeholder="Search by product UPC..." 
                column="product_upc" 
                value={columnFilters.product_upc}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Product Name" 
                placeholder="Search by product name..." 
                column="product_name" 
                value={columnFilters.product_name}
                onChange={handleColumnFilterChange}
              />
              <div className="filter-group">
                <label className="filter-label">Certificate Type</label>
                <select
                  className="filter-input"
                  value={columnFilters.certificate_type}
                  onChange={(e) => handleColumnFilterChange('certificate_type', e.target.value)}
                >
                  <option value="">All certificate types</option>
                  <option value="GCC">GCC</option>
                  <option value="CPC">CPC</option>
                </select>
              </div>
              <FilterInput 
                label="Factory" 
                placeholder="Search by factory name..." 
                column="factory_id" 
                value={columnFilters.factory_id}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="CPSC Lab ID" 
                placeholder="Search by CPSC lab ID..." 
                column="cpsc_lab_id" 
                value={columnFilters.cpsc_lab_id}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Other Lab ID" 
                placeholder="Search by other lab ID..." 
                column="other_lab_id" 
                value={columnFilters.other_lab_id}
                onChange={handleColumnFilterChange}
              />
            </div>
          </div>
        )}
      </div>

      {/* Table Controls */}
      <div className="test-reports-table-header-controls">
        <button className="add-data-btn" onClick={openAddModal}>
          + Add Test Report
        </button>
        <button className="export-csv-btn" onClick={handleExportCSV}>
          Export CSV
        </button>
      </div>

      {/* Test Reports Table */}
      <div className="test-reports-table-wrapper">
        <div className="test-reports-table-scrollable">
          <div className="test-reports-data-table">
          <div className="table-header">
            <div className="test-reports-table-row">
              {tableColumns.filter(col => col.showInTable).map((column) => (
                <TableHeaderCell key={column.key} column={column} />
              ))}
            </div>
          </div>
          
          {sortedTestReports.length === 0 ? (
            <div className="test-reports-table-row">
              <div className="empty-table-message">
                {hasActiveFilters ? (
                  <>
                    <h4>No test reports found</h4>
                    <p>Try adjusting your filter criteria.</p>
                  </>
                ) : (
                  <p style={{color: '#999', fontStyle: 'italic'}}>No test reports yet</p>
                )}
              </div>
            </div>
          ) : (
            paginatedTestReports.map((report: TestReport) => (
              <React.Fragment key={report.id}>
                <div className="test-reports-table-row">
                  {tableColumns.filter(col => col.showInTable).map((column) => (
                    <TableDataCell key={column.key} column={column} report={report} />
                  ))}
                </div>
                
                {/* Product sub-rows */}
                {expandedReports.has(report.id) && report.products && report.products.length > 0 && (
                  report.products.map((product: Product) => (
                    <div key={`${report.id}-product-${product.id}`} className="test-reports-table-row product-row">
                      <div className="table-cell"></div> {/* Empty expand column */}
                      <div className="table-cell product-info" style={{ gridColumn: '2 / -1' }}>
                        <span className="product-upc">
                          {product.upc ? `UPC: ${product.upc}` : 'No UPC'}
                        </span>
                        <span className="product-name">
                          {product.name || 'Unnamed Product'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </React.Fragment>
            ))
          )}
          </div>
        </div>
      </div>

      {/* Pagination */}
      <SmartPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        itemType="test reports"
        onPageChange={handlePageChange}
      />
      </div>

      {/* Help Popup */}
      {popupState.help && (
        <RexiPopup
          message="Manage your test reports here. These reports contain lab testing information required for product certification."
          onClose={() => togglePopup('help', false)}
          confirmText="Got It"
        />
      )}

      {/* Modal Help Popup */}
      {popupState.modalHelp && (
        <RexiPopup
          message={
            editingReport 
              ? `This test report ${selectedProducts.length > 0 ? 'contains products with a ' + getSelectedProductsCertificateType() + ' certificate type' : 'is being edited'}. You can only add products of the same certificate type (either all GCC or all CPC). Once you select your first product, the dropdown will automatically filter to show only products with the same certificate type.\n\nCitation codes are also filtered to show only those applicable to the certificate type.\n\nFor products requiring a CPC, only CPSC certified labs can be selected.`
              : "You can ask me to analyze your test report or manually add it here. Click the Ask Rexi menu button if you would like me to review and extract data from your report.\n\nTest reports can only include products of the same certificate type (either all GCC or all CPC). Once you select your first product, the dropdown will automatically filter to show only products with the same certificate type.\n\nCitation codes will also be filtered to show only those applicable to the certificate type.\n\nFor products requiring a CPC, only CPSC certified labs can be selected."
          }
          onClose={() => togglePopup('modalHelp', false)}
          confirmText="Got It"
        />
      )}

      {/* Delete Confirmation Popup */}
      {popupState.delete && reportToDelete && (
        <RexiPopup
          message="Are you sure you want to delete this test report? This action cannot be undone."
          onClose={() => {
            togglePopup('delete', false);
            setReportToDelete(null);
          }}
          onContinue={handleDeleteTestReport}
          showCancel={true}
        />
      )}

      {/* Foreign Key Error Popup */}
      {popupState.foreignKeyError && (
        <RexiPopup
          message="This test report cannot be deleted because it is currently being used in one or more product certifications. To delete this test report, you must first remove it from any associated product certifications."
          onClose={() => {
            togglePopup('foreignKeyError', false);
            setReportToDelete(null);
          }}
          confirmText="Got It"
        />
      )}

      {/* Add/Edit Test Report Modal */}
      {popupState.modal && (
        <div className="data-modal-overlay" onClick={closeModal}>
          <div className="data-modal" onClick={(e) => e.stopPropagation()}>
            <div className="data-modal-header">
              <h2 className="data-modal-title">
                {editingReport ? 'Test Report Details' : 'Add New Test Report'}
              </h2>
              <button className="modal-close-btn" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="data-modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Report Number <span className="required-indicator">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter report number"
                    value={formData.report_number}
                    onChange={(e) => handleInputChange('report_number', e.target.value)}
                  />
                  {formErrors.report_number && <div className="form-error">{formErrors.report_number}</div>}
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">
                    Products Covered by This Test Report <span className="required-indicator">*</span>
                  </label>
                  {selectedProducts.length > 0 && (
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                      Certificate Type: <strong>{getSelectedProductsCertificateType()}</strong> (all products must be of the same type)
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <select
                      className="form-select"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          addProduct(parseInt(e.target.value));
                          e.target.value = "";
                        }
                      }}
                      style={{ flex: 1 }}
                    >
                      <option value="">Select a product...</option>
                      {getFilteredProducts()
                        .filter((product: Product) => !selectedProducts.some((sp: Product) => sp.id === product.id))
                        .map((product: Product) => (
                          <option key={product.id} value={product.id}>
                            {product.upc ? `UPC: ${product.upc}` : 'No UPC'} - {product.name || 'Unnamed Product'}
                          </option>
                        ))}
                    </select>
                  </div>
                  
                  {selectedProducts.length > 0 && (
                    <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Selected Products:</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        {selectedProducts.map((product: Product) => (
                          <div 
                            key={product.id} 
                            style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '5px 8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '3px',
                              color: '#333'
                            }}
                          >
                            <span style={{ color: '#333' }}>
                              <strong>{product.upc ? `UPC: ${product.upc}` : 'No UPC'}</strong> - {product.name || 'Unnamed Product'}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeProduct(product.id)}
                              style={{
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                padding: '2px 6px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedProducts.length === 0 && (
                    <div style={{ color: '#999', fontStyle: 'italic', fontSize: '14px' }}>
                      No products selected. Please select at least one product.
                    </div>
                  )}
                  
                  {formErrors.products && <div className="form-error">{formErrors.products}</div>}
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">
                    Citation Codes
                  </label>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <select
                      className="form-select"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          addCitationCode(parseInt(e.target.value));
                          e.target.value = "";
                        }
                      }}
                      style={{ flex: 1 }}
                    >
                      <option value="">Select a citation code...</option>
                      {getFilteredCitationCodes()
                        .filter((citationCode: CitationCode) => !selectedCitationCodes.some((scc: CitationCode) => scc.id === citationCode.id))
                        .map((citationCode: CitationCode) => (
                          <option key={citationCode.id} value={citationCode.id}>
                            Code: {citationCode.citation_code} - {citationCode.citation_code_description}
                          </option>
                        ))}
                    </select>
                  </div>
                  
                  {selectedCitationCodes.length > 0 && (
                    <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Selected Citation Codes:</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        {selectedCitationCodes.map((citationCode: CitationCode) => (
                          <div 
                            key={citationCode.id} 
                            style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '5px 8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '3px',
                              color: '#333'
                            }}
                          >
                            <span style={{ color: '#333' }}>
                              <strong>Code: {citationCode.citation_code}</strong> - {citationCode.citation_code_description}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeCitationCode(citationCode.id)}
                              style={{
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                padding: '2px 6px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedCitationCodes.length === 0 && (
                    <div style={{ color: '#999', fontStyle: 'italic', fontSize: '14px' }}>
                      No citation codes selected.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Factory</label>
                  <select
                    className="form-select"
                    value={formData.factory_id}
                    onChange={(e) => handleInputChange('factory_id', e.target.value)}
                  >
                    <option value="">Select a factory...</option>
                    {factories.map((factory: Factory) => (
                      <option key={factory.id} value={factory.id}>
                        {factory.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Test Lab
                    {getSelectedProductsCertificateType() === 'CPC' && (
                      <span className="required-indicator">*</span>
                    )}
                  </label>
                  {getSelectedProductsCertificateType() === 'CPC' && (
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                      <strong>CPSC certified lab required</strong> for CPC certificate type products
                    </div>
                  )}
                  <LabSelector
                    selectedLab={selectedLab}
                    onLabSelect={(lab) => {
                      setSelectedLab(lab);
                      // Clear lab validation error if it exists
                      if (formErrors.lab) {
                        setFormErrors((prev: Record<string, string>) => ({ ...prev, lab: '' }));
                      }
                    }}
                    placeholder="Search for a test lab..."
                    className="form-input"
                    certificateType={getSelectedProductsCertificateType()}
                  />
                  {formErrors.lab && <div className="form-error">{formErrors.lab}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Report Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.report_date}
                    onChange={(e) => handleInputChange('report_date', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="data-modal-footer">
              <button 
                className="modal-btn modal-btn-cancel" 
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                className="modal-btn modal-btn-save" 
                onClick={handleSaveTestReport}
                disabled={saving}
              >
                {saving ? 'Saving...' : (editingReport ? 'Update Test Report' : 'Add Test Report')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestReports;
